import { ActionButton } from '@/components/common/ActionButton';
import { WarningIcon } from '@/components/common/icons';
import { useBackendLabel } from '@/components/documents/labels';
import type { StepModel } from '@/types/documents.types';
import { stepLabelKey } from '@/utils/categories';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface SkippedPagesWarningProps {
  /** The steps of the finished run (`DocumentModel.detection.steps`). */
  steps: StepModel[];
  onGoToPage: (page: number) => void;
  /** Offered when the caller can re-run the skipped pages; the button is held while `isRetrying`. */
  onRetry?: () => void;
  isRetrying?: boolean;
}

/**
 * The pages a detection step could not analyse (an LLM timeout, an overlong answer). The run still
 * finished, and a page nobody analysed looks exactly like a clean one, so this strip stays for the
 * whole review: one line per step, each page number turning to its page, and — given `onRetry` —
 * the button that has the backend analyse just those pages again. Nothing without skipped pages.
 */
export const SkippedPagesWarning: FC<SkippedPagesWarningProps> = ({ steps, onGoToPage, onRetry, isRetrying }) => {
  const { t } = useTranslation();
  const backendLabel = useBackendLabel();
  const affected = steps.filter((step) => step.skipped_pages?.length);
  if (affected.length === 0) return null;

  return (
    <div
      role="alert"
      className="flex flex-none flex-col gap-1 border-b border-line bg-warn-soft px-5 py-2 text-[11.5px] leading-[1.5] text-ink"
    >
      {affected.map((step) => {
        const pages = step.skipped_pages ?? [];
        return (
          <p key={step.key} className="m-0 flex items-start gap-[9px]">
            <WarningIcon size={13} className="mt-px flex-none text-warn" />
            <span>
              {t('review.skippedPages.text', {
                count: pages.length,
                step: backendLabel(step.label_cs, stepLabelKey(step.key)),
              })}
              {pages.map((page, index) => (
                <span key={page}>
                  {index > 0 ? ', ' : ' '}
                  <button
                    type="button"
                    onClick={() => onGoToPage(page)}
                    aria-label={t('review.skippedPages.goTo', { page })}
                    className="cursor-pointer border-none bg-transparent p-0 font-semibold text-primary underline hover:text-accent"
                  >
                    {page}
                  </button>
                </span>
              ))}
            </span>
          </p>
        );
      })}
      {onRetry ? (
        // Indented past the icon so the button lines up with the text; the label swap is what a
        // screen reader hears from this alert while the pages are being analysed.
        <div className="pl-[22px]">
          <ActionButton loading={isRetrying} onClick={onRetry}>
            {t(isRetrying ? 'review.skippedPages.retrying' : 'review.skippedPages.retry')}
          </ActionButton>
        </div>
      ) : null}
    </div>
  );
};
